import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { mensajeDeError, pesos } from '../lib/api';
import { AdminOperacion } from '../components/AdminOperacion';
import { AdminPostulaciones } from '../components/AdminPostulaciones';
import { AdminMetricas } from '../components/AdminMetricas';
import { AdminPagos } from '../components/AdminPagos';
import {
  Shield,
  LogOut,
  Users,
  Calendar,
  DollarSign,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  Eye,
  EyeOff,
  Search,
  RefreshCw,
  UserCheck,
  UserX,
  Key,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';


const AdminDashboard = () => {
  const navigate = useNavigate();
  const [psychologists, setPsychologists] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedPsy, setSelectedPsy] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    specialties: '',
    languages: 'Español',
    bio: '',
    education: '',
    price_from: 30000,
    profile_image: '',
    registration_number: '',
    verified: false
  });
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // El token de administrador lo adjunta el cliente compartido de api.js
  // según la ruta, así que aquí ya no hace falta armar cabeceras a mano.
  const checkAuth = useCallback(() => {
    const token = sessionStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return false;
    }
    return true;
  }, [navigate]);

  const fetchData = useCallback(async () => {
    if (!checkAuth()) return;
    
    setLoading(true);
    try {
      const [psyResponse, statsResponse] = await Promise.all([
        api.get(`/admin/psicologos`),
        api.get(`/admin/stats`)
      ]);
      setPsychologists(psyResponse.data);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        sessionStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  }, [checkAuth, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      specialties: '',
      languages: 'Español',
      bio: '',
      education: '',
      price_from: 30000,
      profile_image: '',
      registration_number: '',
      verified: false
    });
  };

  const handleCreate = async () => {
    try {
      const payload = {
        ...formData,
        specialties: formData.specialties.split(',').map(s => s.trim()).filter(Boolean),
        languages: formData.languages.split(',').map(s => s.trim()).filter(Boolean),
        education: formData.education.split(',').map(s => s.trim()).filter(Boolean)
      };
      
      await api.post(`/admin/psicologos`, payload);
      toast.success('Psicólogo creado exitosamente');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating psychologist:', error);
      toast.error(error.response?.data?.detail || 'Error al crear psicólogo');
    }
  };

  const handleEdit = async () => {
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        specialties: formData.specialties.split(',').map(s => s.trim()).filter(Boolean),
        languages: formData.languages.split(',').map(s => s.trim()).filter(Boolean),
        bio: formData.bio,
        education: formData.education.split(',').map(s => s.trim()).filter(Boolean),
        price_from: formData.price_from,
        profile_image: formData.profile_image,
        registration_number: formData.registration_number,
        verified: formData.verified
      };
      
      await api.patch(`/admin/psicologos/${selectedPsy.id}`, payload);
      toast.success('Psicólogo actualizado');
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      console.error('Error updating psychologist:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/psicologos/${selectedPsy.id}`);
      toast.success('Psicólogo eliminado');
      setShowDeleteModal(false);
      setSelectedPsy(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting psychologist:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleResetPassword = async () => {
    try {
      await api.post(
        `/admin/psicologos/${selectedPsy.id}/reset-password`,
        {},
        { params: { new_password: newPassword } }
      );
      toast.success('Contraseña actualizada');
      setShowResetPasswordModal(false);
      setNewPassword('');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  /**
   * Suspende o reactiva a un profesional.
   * Suspender no borra nada: deja de aparecer y no puede entrar, pero sus
   * sesiones ya agendadas siguen en pie hasta que se decida qué hacer con ellas.
   */
  const alternarSuspension = async (psy) => {
    const suspender = Boolean(psy.available);

    try {
      const { data } = await api.post(`/admin/psicologos/${psy.id}/suspender`, { suspend: suspender });
      toast.success(data.message);
      if (data.warning) toast.warning(data.warning, { duration: 9000 });
      fetchData();
    } catch (error) {
      toast.error(mensajeDeError(error, 'No pudimos cambiar el estado'));
    }
  };

  const openEditModal = (psy) => {
    setSelectedPsy(psy);
    setFormData({
      name: psy.name,
      email: psy.email,
      password: '',
      specialties: psy.specialties?.join(', ') || '',
      languages: psy.languages?.join(', ') || 'Español',
      bio: psy.bio || '',
      education: psy.education?.join(', ') || '',
      price_from: psy.price_from || 30000,
      profile_image: psy.profile_image || '',
      registration_number: psy.registration_number || '',
      verified: psy.verified || false
    });
    setShowEditModal(true);
  };

  const filteredPsychologists = psychologists.filter(psy =>
    psy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    psy.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-600/20 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Panel de Administración</h1>
              <p className="text-sm text-slate-400">AnonimaMente - Super Admin</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={handleLogout}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Stats */}
        {stats && (
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-500/20 rounded-lg">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Psicólogos</p>
                    <p className="text-2xl font-bold text-white">{stats.psychologists.total}</p>
                    <p className="text-xs text-slate-400">{stats.psychologists.verified} verificados</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-500/20 rounded-lg">
                    <Calendar className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Reservas</p>
                    <p className="text-2xl font-bold text-white">{stats.bookings.total}</p>
                    <p className="text-xs text-slate-400">{stats.bookings.confirmed} confirmadas</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-500/20 rounded-lg">
                    <DollarSign className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Ingresos</p>
                    <p className="text-2xl font-bold text-white">
                      ${stats.revenue.total.toLocaleString('es-CL')}
                    </p>
                    <p className="text-xs text-slate-400">CLP total</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-500/20 rounded-lg">
                    <UserCheck className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Por Verificar</p>
                    <p className="text-2xl font-bold text-white">{stats.psychologists.pending_verification}</p>
                    <p className="text-xs text-slate-400">pendientes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Avisos que requieren acción */}
        {stats?.operations && (
          <div className="flex flex-wrap gap-3">
            {stats.operations.pending_applications > 0 && (
              <Aviso
                texto={`${stats.operations.pending_applications} postulación(es) esperando revisión`}
                tono="teal"
              />
            )}
            {stats.operations.without_schedule > 0 && (
              <Aviso
                texto={`${stats.operations.without_schedule} profesional(es) sin horario configurado`}
                tono="amber"
              />
            )}
            {stats.operations.unhandled_messages > 0 && (
              <Aviso
                texto={`${stats.operations.unhandled_messages} mensaje(s) sin responder`}
                tono="amber"
              />
            )}
            {stats.operations.failed_emails > 0 && (
              <Aviso texto={`${stats.operations.failed_emails} correo(s) fallidos`} tono="red" />
            )}
            {stats.operations.refunds_pending > 0 && (
              <Aviso
                texto={`${stats.operations.refunds_pending} pago(s) por devolver: entraron sobre reservas ya canceladas`}
                tono="red"
              />
            )}
          </div>
        )}

        {/* Estado de la pasarela de pagos */}
        <AdminPagos />

        {/* Postulaciones de nuevos profesionales */}
        <AdminPostulaciones onCambio={fetchData} />

        {/* Métricas y exportación */}
        <AdminMetricas />

        {/* Reservas, mensajes, correos y ajustes */}
        <AdminOperacion />

        {/* Psychologists Management */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white">Gestión de Psicólogos</CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchData}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button 
                onClick={() => { resetForm(); setShowCreateModal(true); }}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="create-psychologist-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Psicólogo
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                  data-testid="search-psychologists"
                />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Profesional</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Email</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Especialidades</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Precio</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Estado</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPsychologists.map((psy) => (
                    <tr key={psy.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <img 
                            src={psy.profile_image || 'https://via.placeholder.com/40'} 
                            alt={psy.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                          <div>
                            <p className="text-white font-medium">{psy.name}</p>
                            <p className="text-xs text-slate-400">{psy.registration_number || 'Sin registro'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-300">{psy.email}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {psy.specialties?.slice(0, 2).map((s, i) => (
                            <Badge key={i} variant="secondary" className="text-xs bg-slate-700 text-slate-300">
                              {s}
                            </Badge>
                          ))}
                          {psy.specialties?.length > 2 && (
                            <Badge variant="secondary" className="text-xs bg-slate-700 text-slate-300">
                              +{psy.specialties.length - 2}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-300">
                        ${psy.price_from?.toLocaleString('es-CL')}
                      </td>
                      <td className="py-3 px-4">
                        {psy.verified ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            <UserCheck className="w-3 h-3 mr-1" />
                            Verificado
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                            <UserX className="w-3 h-3 mr-1" />
                            Pendiente
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditModal(psy)}
                            className="text-slate-400 hover:text-white hover:bg-slate-700"
                            data-testid={`edit-psy-${psy.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => { setSelectedPsy(psy); setShowResetPasswordModal(true); }}
                            className="text-slate-400 hover:text-amber-400 hover:bg-slate-700"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => alternarSuspension(psy)}
                            className={`hover:bg-slate-700 ${psy.available ? 'text-slate-400 hover:text-amber-400' : 'text-amber-400 hover:text-green-400'}`}
                            title={psy.available ? 'Suspender' : 'Reactivar'}
                            data-testid={`suspend-psy-${psy.id}`}
                          >
                            {psy.available ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedPsy(psy); setShowDeleteModal(true); }}
                            className="text-slate-400 hover:text-red-400 hover:bg-slate-700"
                            data-testid={`delete-psy-${psy.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredPsychologists.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  No se encontraron psicólogos
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Crear Nuevo Psicólogo</DialogTitle>
            <DialogDescription className="text-slate-400">
              Ingresa los datos del nuevo profesional
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Nombre completo *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="Dr. Juan Pérez"
                data-testid="input-psy-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="email@ejemplo.com"
                data-testid="input-psy-email"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Contraseña *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white pr-10"
                  placeholder="••••••••"
                  data-testid="input-psy-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">N° Registro Profesional</Label>
              <Input
                value={formData.registration_number}
                onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="12345-CL"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-slate-300">Especialidades (separadas por coma)</Label>
              <Input
                value={formData.specialties}
                onChange={(e) => setFormData({...formData, specialties: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="Ansiedad, Depresión, Estrés"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Idiomas</Label>
              <Input
                value={formData.languages}
                onChange={(e) => setFormData({...formData, languages: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="Español, Inglés"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Precio desde (CLP)</Label>
              <Input
                type="number"
                value={formData.price_from}
                onChange={(e) => setFormData({...formData, price_from: parseInt(e.target.value) || 0})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-slate-300">Biografía</Label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                className="w-full h-20 px-3 py-2 rounded-md bg-slate-700 border border-slate-600 text-white"
                placeholder="Descripción profesional..."
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-slate-300">Educación (separada por coma)</Label>
              <Input
                value={formData.education}
                onChange={(e) => setFormData({...formData, education: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="Universidad X, Magíster en Y"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-slate-300">URL Imagen de Perfil</Label>
              <Input
                value={formData.profile_image}
                onChange={(e) => setFormData({...formData, profile_image: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="https://..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-slate-600 text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleCreate} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-create-psy">
              <Check className="w-4 h-4 mr-2" />
              Crear Psicólogo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Psicólogo</DialogTitle>
            <DialogDescription className="text-slate-400">
              Modifica los datos del profesional
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Nombre completo</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">N° Registro</Label>
              <Input
                value={formData.registration_number}
                onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Precio desde (CLP)</Label>
              <Input
                type="number"
                value={formData.price_from}
                onChange={(e) => setFormData({...formData, price_from: parseInt(e.target.value) || 0})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-slate-300">Especialidades</Label>
              <Input
                value={formData.specialties}
                onChange={(e) => setFormData({...formData, specialties: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-slate-300">Idiomas</Label>
              <Input
                value={formData.languages}
                onChange={(e) => setFormData({...formData, languages: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-slate-300">Biografía</Label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                className="w-full h-20 px-3 py-2 rounded-md bg-slate-700 border border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-slate-300">Educación</Label>
              <Input
                value={formData.education}
                onChange={(e) => setFormData({...formData, education: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-slate-300">URL Imagen</Label>
              <Input
                value={formData.profile_image}
                onChange={(e) => setFormData({...formData, profile_image: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <Checkbox
                id="verified"
                checked={formData.verified}
                onCheckedChange={(checked) => setFormData({...formData, verified: checked})}
              />
              <Label htmlFor="verified" className="text-slate-300 cursor-pointer">
                Marcar como verificado
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-slate-600 text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleEdit} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-edit-psy">
              <Check className="w-4 h-4 mr-2" />
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400">Eliminar Psicólogo</DialogTitle>
            <DialogDescription className="text-slate-400">
              ¿Estás seguro de que deseas eliminar a <strong className="text-white">{selectedPsy?.name}</strong>?
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="border-slate-600 text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-psy">
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetPasswordModal} onOpenChange={setShowResetPasswordModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Cambiar Contraseña</DialogTitle>
            <DialogDescription className="text-slate-400">
              Nueva contraseña para <strong className="text-white">{selectedPsy?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="text-slate-300">Nueva Contraseña</Label>
            <div className="relative mt-2">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white pr-10"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPasswordModal(false)} className="border-slate-600 text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleResetPassword} className="bg-amber-600 hover:bg-amber-700">
              <Key className="w-4 h-4 mr-2" />
              Cambiar Contraseña
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/** Aviso corto de algo que requiere atencion del administrador. */
const Aviso = ({ texto, tono }) => {
  const tonos = {
    teal: 'bg-teal-500/15 text-teal-300 border-teal-500/30',
    amber: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    red: 'bg-red-500/15 text-red-300 border-red-500/30',
  };

  return (
    <div className={`px-4 py-2 rounded-lg border text-sm ${tonos[tono] || tonos.teal}`}>
      {texto}
    </div>
  );
};

export default AdminDashboard;
